/**
 * Utility to track field updates in different components for sharing
 */

// Storage key for tracking updates
const TRACKED_UPDATES_KEY = 'tracked_updates';

/**
 * Track a field update
 * @param {string} component - Component identifier (e.g., 'oneLink', 'community', 'companyProfile')
 * @param {string} field - Field name that was updated
 * @param {any} oldValue - Previous value (can be null if it's a new addition)
 * @param {any} newValue - New value
 * @param {string} displayName - Human-readable field name for display
 * @param {string} entityId - Optional ID of the entity being updated (e.g., communityId)
 */
export const trackUpdate = (component, field, oldValue, newValue, displayName, entityId = null) => {
  try {
    const trackedUpdates = getTrackedUpdates();
    
    // Define update action (edit, add, delete)
    let action;
    if (oldValue === null || oldValue === undefined) {
      action = 'added';
    } else if (newValue === null || newValue === undefined) {
      action = 'deleted';
    } else {
      action = 'updated';
    }
    
    // Add update to tracked updates
    trackedUpdates.push({
      component,
      field,
      displayName,
      action,
      oldValue,
      newValue,
      timestamp: new Date().toISOString(),
      entityId
    });
    
    // Save back to storage
    localStorage.setItem(TRACKED_UPDATES_KEY, JSON.stringify(trackedUpdates));
  } catch (error) {
    console.error('Error tracking update:', error);
  }
};

/**
 * Get all tracked updates
 * @returns {Array} - Array of tracked updates
 */
export const getTrackedUpdates = () => {
  try {
    const updates = localStorage.getItem(TRACKED_UPDATES_KEY);
    return updates ? JSON.parse(updates) : [];
  } catch (error) {
    console.error('Error retrieving tracked updates:', error);
    return [];
  }
};

/**
 * Clear all tracked updates
 */
export const clearTrackedUpdates = () => {
  localStorage.removeItem(TRACKED_UPDATES_KEY);
};

/**
 * Clear tracked updates for a specific component
 * @param {string} component - Component identifier
 */
export const clearComponentUpdates = (component) => {
  try {
    const updates = getTrackedUpdates();
    const filteredUpdates = updates.filter(update => update.component !== component);
    localStorage.setItem(TRACKED_UPDATES_KEY, JSON.stringify(filteredUpdates));
  } catch (error) {
    console.error('Error clearing component updates:', error);
  }
};

/**
 * Generate a summary of tracked updates for a component
 * @param {string} component - Component identifier
 * @returns {string} - A formatted summary of updates with styling
 */
export const generateUpdateSummary = (component) => {
  const updates = getTrackedUpdates().filter(update => update.component === component);
  
  if (updates.length === 0) {
    return '';
  }
  
  // Different headline formats based on component type
  let headline = '';
  if (component === 'oneLink') {
    headline = '<h3 style="color: #fd5901; margin-bottom: 10px;">🚀 OneLink Updates Spotlight 🚀</h3>';
  } else if (component === 'community') {
    headline = '<h3 style="color: #30b567; margin-bottom: 10px;">🌿 Community Updates Highlights 🌿</h3>';
  } else if (component === 'companyProfile') {
    headline = '<h3 style="color: #4a6fdc; margin-bottom: 10px;">💼 Company Profile Refresh 💼</h3>';
  } else {
    headline = '<h3 style="color: #fd5901; margin-bottom: 10px;">✨ Latest Profile Updates ✨</h3>';
  }
  
  let summary = headline + '<div style="font-family: Arial, sans-serif;">';
  
  // Group updates by type for better organization
  const updatesGrouped = {
    introMessage: updates.filter(u => u.field === 'introductoryMessage'),
    documents: updates.filter(u => u.field === 'document'),
    termsConditions: updates.filter(u => u.field.includes('terms')),
    teamMembers: updates.filter(u => u.field === 'team'),
    profileInfo: updates.filter(u => !['introductoryMessage', 'document', 'team'].includes(u.field) && !u.field.includes('terms'))
  };
  
  // Special intro for introductory message
  if (updatesGrouped.introMessage.length > 0) {
    summary += `<div style="margin-bottom: 12px; padding: 8px; border-left: 3px solid #fd5901; background-color: #fff9f5;">
      <p style="font-weight: bold; margin-bottom: 5px;">📝 Introductory Message Updated</p>
      <p style="font-style: italic; color: #555;">"Your profile's first impression has been refreshed to better showcase your vision."</p>
    </div>`;
  }
  
  // Document uploads with improved styling
  if (updatesGrouped.documents.length > 0) {
    summary += `<div style="margin-bottom: 12px; padding: 8px; border-left: 3px solid #4a6fdc; background-color: #f5f8ff;">
      <p style="font-weight: bold; margin-bottom: 5px;">📄 Document Updates</p>
      <ul style="margin: 0; padding-left: 20px;">`;
      
    updatesGrouped.documents.forEach(update => {
      const folderInfo = update.displayName.includes('to') 
        ? `to <span style="color: #4a6fdc; font-weight: bold;">${update.displayName.split('to ')[1]}</span>` 
        : '';
      summary += `<li>Added <span style="font-weight: bold;">${update.newValue}</span> ${folderInfo}</li>`;
    });
    
    summary += `</ul></div>`;
  }
  
  // Terms and conditions with highlighting
  if (updatesGrouped.termsConditions.length > 0) {
    summary += `<div style="margin-bottom: 12px; padding: 8px; border-left: 3px solid #30b567; background-color: #f5fff8;">
      <p style="font-weight: bold; margin-bottom: 5px;">📋 Terms & Conditions Updated</p>
      <p>Updated ${updatesGrouped.termsConditions.length} community guidelines to better serve our members.</p>
    </div>`;
  }
  
  // Team members with avatars (placeholder)
  if (updatesGrouped.teamMembers.length > 0) {
    summary += `<div style="margin-bottom: 12px; padding: 8px; border-left: 3px solid #f7b731; background-color: #fffcf5;">
      <p style="font-weight: bold; margin-bottom: 5px;">👥 Team Updates</p>
      <p>Welcomed new talent to the team! ${updatesGrouped.teamMembers.map(u => `<span style="font-weight: bold;">${u.newValue}</span>`).join(', ')}</p>
    </div>`;
  }
  
  // Other profile fields
  if (updatesGrouped.profileInfo.length > 0) {
    summary += `<div style="margin-bottom: 12px; padding: 8px; border-left: 3px solid #8e44ad; background-color: #f9f5ff;">
      <p style="font-weight: bold; margin-bottom: 5px;">🔄 Profile Information Updates</p>
      <ul style="margin: 0; padding-left: 20px;">`;
      
    updatesGrouped.profileInfo.forEach(update => {
      summary += `<li>Updated <span style="font-weight: bold;">${update.displayName}</span>`;
      
      // If we have both old and new values, show a transition
      if (update.oldValue && update.newValue && update.action === 'updated') {
        summary += `: <span style="text-decoration: line-through; color: #888;">${update.oldValue}</span> ➔ <span style="color: #45a049;">${update.newValue}</span>`;
      }
      
      summary += `</li>`;
    });
    
    summary += `</ul></div>`;
  }
  
  // Add a nice footer
  const footerTexts = [
    "Excited to share these updates with my network!",
    "Constantly evolving and improving!",
    "Thanks for following my journey!",
    "Making progress every day!"
  ];
  
  const randomFooter = footerTexts[Math.floor(Math.random() * footerTexts.length)];
  summary += `<p style="font-style: italic; text-align: center; margin-top: 15px; color: #666;">${randomFooter}</p>`;
  
  // Close the main div and add hashtags
  summary += '</div>';
  
  // Add some relevant hashtags based on the component
  if (component === 'oneLink') {
    summary += '<p style="margin-top: 15px; color: #666;">#OneLink #ProfileUpdates #ProfessionalGrowth</p>';
  } else if (component === 'community') {
    summary += '<p style="margin-top: 15px; color: #666;">#CommunityGrowth #NetworkUpdates #BetterTogether</p>';
  } else if (component === 'companyProfile') {
    summary += '<p style="margin-top: 15px; color: #666;">#CompanyNews #StartupJourney #GrowthMindset</p>';
  }
  
  return summary;
}; 